import { FC, useMemo, useState } from 'react';

import { BUTTON_TYPES, OptionType, TITLE_MODES, TOAST_STATUS } from '@evinced-private/ui-common';

import { IWfaLabel } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { transformCoreLabelsToCustomLabels } from 'src/common/helpers/LabelDisplayHelpers';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useNotifications } from 'src/common/providers/notificationsProvider/NotificationsConsumer';
import { getFromLocalStorage, saveToLocalStorage } from 'src/common/services/api/LocalStorageApi';
import { Logger } from 'src/common/services/Logger';
import { ILabelConfig } from 'src/common/types/LabelsTypes';

import { PlatformPopup } from '../../../../common/components/platform-popup/PlatformPopup';
import { getSortedLabelsConfig } from '../../pages/sessions/store';
import { WFA_SESSIONS_LABEL_KEY_ORDER } from '../../pages/sessions/WfaSessionsPage.constants';
import { updateSession } from '../../services/wfa-session-update-service/WfaSessionUpdateService';

import { EditSessionForm } from './EditSessionForm';
import { EDIT_SESSION_POPUP_SHOW_INFO_MESSAGE } from './EditSessionPopup.constants';
import { EDIT_SESSION_POPUP_LABELS } from './EditSessionPopup.labels';
import { IEditSessionPopup } from './EditSessionPopup.types';

import './EditSessionPopup.scss';

export const EditSessionPopup: FC<IEditSessionPopup> = ({
	isOpen = false,
	sessionName,
	sessionId,
	labels,
	onClose,
	onSubmit
}) => {
	const notificationsContext = useNotifications();
	const { configuration } = useConfiguration();

	const [name, setName] = useState(sessionName);
	const [selectedLabels, setSelectedLabels] = useState(labels || []);
	const [showInfoMessage, setShowInfoMessage] = useState(
		getFromLocalStorage(EDIT_SESSION_POPUP_SHOW_INFO_MESSAGE) ?? true
	);

	const isLabelsToggleEnabled = (configuration.WFA?.TOGGLES as Record<string, boolean>)
		?.ENABLE_PLATFORM_LABELS as boolean;

	const labelsList = useMemo(
		() => [
			...(configuration?.PLATFORM?.ADDITIONAL_LABELS || []),
			...(configuration?.PLATFORM?.CORE_LABELS || [])
		],
		[configuration?.PLATFORM?.ADDITIONAL_LABELS, configuration?.PLATFORM?.CORE_LABELS]
	);
	const availableLabelsList: ILabelConfig[] = useMemo(
		() => transformCoreLabelsToCustomLabels(labelsList),
		[labelsList]
	);

	const sortedAvailableLabelsList = useMemo(
		() => getSortedLabelsConfig(availableLabelsList, WFA_SESSIONS_LABEL_KEY_ORDER),
		[availableLabelsList]
	);

	const dismissMessage = (): void => {
		saveToLocalStorage(EDIT_SESSION_POPUP_SHOW_INFO_MESSAGE, false);
		setShowInfoMessage(false);
	};

	const onConfirm = async (): Promise<void> => {
		try {
			await onSubmit?.(name, selectedLabels);
			await updateSession(sessionId, name, labels, selectedLabels);

			notificationsContext.toast({
				show: true,
				status: TOAST_STATUS.SUCCESS,
				announcement: EDIT_SESSION_POPUP_LABELS.UPDATE_SUCCESS
			});
		} catch (e) {
			Logger.error('Error updating session', e);
			notificationsContext.toast({
				show: true,
				status: TOAST_STATUS.FAIL,
				announcement: EDIT_SESSION_POPUP_LABELS.UPDATE_FAILURE
			});
		} finally {
			onClose();
		}
	};

	const onCancel = (): void => {
		onClose();
	};

	const handleChange = (labelConfig: ILabelConfig, newValue: OptionType | OptionType[]): void => {
		const newValuesNormalized = [].concat(newValue);

		setSelectedLabels((prev) => {
			const prevSelectedLabels = [...prev];

			const prevValuesWithoutTheChanged = prevSelectedLabels.filter(
				(prevValue) => prevValue.key !== labelConfig.key
			);

			if (newValuesNormalized.length) {
				const newLabel: IWfaLabel = {
					name: labelConfig.name,
					key: labelConfig.key,
					values: newValuesNormalized.map(({ label, value }) => ({ key: value, name: label }))
				};

				return [...prevValuesWithoutTheChanged, newLabel];
			}

			return prevValuesWithoutTheChanged;
		});
	};

	const buttons = [
		{
			onClick: onCancel,
			type: BUTTON_TYPES.GHOST,
			title: EDIT_SESSION_POPUP_LABELS.CANCEL_BUTTON
		},
		{
			onClick: onConfirm,
			type: BUTTON_TYPES.PRIMARY,
			title: EDIT_SESSION_POPUP_LABELS.SAVE_BUTTON
		}
	];

	return (
		<PlatformPopup
			popupWidth={620}
			className="edit-session-popup"
			titleMode={TITLE_MODES.NORMAL}
			title={EDIT_SESSION_POPUP_LABELS.EDIT_SESSION_TITLE}
			isOpen={isOpen}
			onClose={onClose}
			buttons={buttons}
			isCentered
			isControlled
			isBgTransparent
		>
			<EditSessionForm
				name={name}
				onNameChange={setName}
				showInfoMessage={showInfoMessage}
				onDismissMessage={dismissMessage}
				isLabelsToggleEnabled={isLabelsToggleEnabled}
				availableLabels={sortedAvailableLabelsList}
				selectedLabels={selectedLabels}
				onLabelChange={handleChange}
			/>
		</PlatformPopup>
	);
};
